import { useState } from "react";
import axios from "axios";
import { Heart } from "lucide-react";
import { getFingerprint } from "@/lib/fingerprint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Heart-shaped like button overlay for menu cards on a location page.
 *
 * Optimistic UI: count + liked state update instantly, revert if the server rejects.
 * Single source of truth: parent passes initial counts; this component manages local state.
 */
export default function LikeButton({ slug, itemId, accessCode, initialLiked, initialCount }) {
  const [liked, setLiked] = useState(!!initialLiked);
  const [count, setCount] = useState(initialCount || 0);
  const [pending, setPending] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pending || !accessCode) return;

    // Optimistic
    const nextLiked = !liked;
    const nextCount = nextLiked ? count + 1 : Math.max(0, count - 1);
    setLiked(nextLiked);
    setCount(nextCount);
    setPending(true);

    try {
      const res = await axios.post(
        `${API}/locations/${slug}/items/${itemId}/like`,
        {},
        {
          headers: {
            "X-Location-Code": accessCode,
            "X-User-Fingerprint": getFingerprint(),
          },
        }
      );
      // Sync from server (truth)
      setLiked(res.data.liked);
      setCount(res.data.count);
    } catch {
      // Revert
      setLiked(liked);
      setCount(count);
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={pending}
      className={`absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full backdrop-blur-sm transition-all ${
        liked
          ? "bg-white/90 text-red-500 shadow-md scale-105"
          : "bg-black/40 text-white hover:bg-black/60"
      } ${pending ? "opacity-70" : ""}`}
      aria-label={liked ? "Unlike this item" : "Like this item"}
      data-testid={`like-button-${itemId}`}
    >
      <Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} strokeWidth={2.5} />
      {count > 0 && <span className="text-xs font-semibold">{count}</span>}
    </button>
  );
}
